import { child, push, ref, remove, set, update } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { useObjectVal } from "react-firebase-hooks/database";
import { fbDatabase, fbFunctions } from "../firebase";
import { logBoardCreated } from "./analytics";
import { createSession } from "./session";
import { refUserBoards } from "./user";
import { boardTransform, type BoardData, type BoardAccessResult } from "./board.types";

export * from "./board.types";

const boardOptions = {
  keyField: 'uid',
  transform: boardTransform,
};

function refBoards() { return ref(fbDatabase, `boards/`) } // private, no need for all boards
export function refBoard(boardUid: string) { return child(refBoards(), boardUid) }
export function refBoardMembers(boardUid: string) { return child(refBoard(boardUid), 'memberUids') }

export function useBoard(boardUid: string) { return useObjectVal<BoardData>(refBoard(boardUid), boardOptions) }

export const createBoard: (
  ownerUid: string,
  title: string,
  description: string | null,
  isPrivate?: boolean,
) => Promise<string | null> = async (ownerUid, title, description, isPrivate = false) => {
  const newBoardRef = await push(refBoards());
  if (!newBoardRef.key) return null;

  const epoch = Date.now();
  await set(newBoardRef, {
    memberUids: { [ownerUid]: epoch },
    ownerUid: ownerUid,
    createdDate: epoch,
    title: title,
    description: description,
    isPrivate: isPrivate,
  });

  await updateBoardMembership(ownerUid, newBoardRef.key);
  await createSession(newBoardRef.key, false);

  logBoardCreated();
  return newBoardRef.key;
}

export const editBoard: (
  boardData: Pick<BoardData, 'uid'>,
  title: string,
  description: string | null,
  isPrivate: boolean,
) => Promise<void> = async (boardData, title, description, isPrivate) => {
  await update(refBoard(boardData.uid), {
    title: title,
    description: description,
    isPrivate: isPrivate,
  });
}

export const updateBoardMembership: (userUid: string, boardUid: string) => Promise<void> = async (userUid, boardUid) => {
  const epoch = Date.now();
  await set(child(refBoardMembers(boardUid), userUid), epoch);
  await set(child(refUserBoards(userUid), boardUid), epoch);
}

export const removeBoardMembership: (boardUid: string, userUid: string) => Promise<void> = async (boardUid, userUid) => {
  await remove(child(refBoardMembers(boardUid), userUid));
  await remove(child(refUserBoards(userUid), boardUid));
}

export async function shredBoard(boardUid: string): Promise<void> {
  const fn = httpsCallable<{ boardUid: string }, { success: boolean }>(fbFunctions, "shredBoard");
  await fn({ boardUid });
}

// Callable function wrappers

export async function checkBoardAccess(boardUid: string): Promise<BoardAccessResult> {
  const fn = httpsCallable<{ boardUid: string }, BoardAccessResult>(fbFunctions, "checkBoardAccess");
  const result = await fn({ boardUid });
  return result.data;
}

export async function joinBoardAsNewMember(boardUid: string): Promise<boolean> {
  const fn = httpsCallable<{ boardUid: string }, { success: boolean }>(fbFunctions, "joinBoard");
  const result = await fn({ boardUid });
  return result.data.success;
}

export async function claimBoardOwnership(boardUid: string): Promise<boolean> {
  const fn = httpsCallable<{ boardUid: string }, { success: boolean }>(fbFunctions, "claimBoardOwnership");
  const result = await fn({ boardUid });
  return result.data.success;
}
